import React, { useMemo, useState } from 'react';
import { Holding, Currency, Loan, LiquidityEvent } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { Modal } from './ui/Modal';
import { Building2, Calculator, Plus, Info } from 'lucide-react';
import { COUNTRIES, CURRENCIES, getDefaultCurrency } from '../utils/countries';
import { formatCurrency } from '../utils/currency';
import { calculateMonthlyPayment, calculateTotalInterest } from '../utils/loans';
import { LoanForm } from './LoanForm';
import { LiquidityEventForm } from './LiquidityEventForm';
interface RealEstateFormProps {
  onAdd: (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => void;
  partnerNames: [string, string];
  loans: Loan[];
  liquidityEvents: LiquidityEvent[];
  holdings?: Holding[];
  onAddLoan?: (loan: Omit<Loan, 'id' | 'createdAt'>) => string | void;
  onAddEvent?: (event: Omit<LiquidityEvent, 'id'>) => string | void;
  baseCurrency?: Currency;
  baseCountry?: string;
  editingHolding?: Holding;
  onEdit?: (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => void;
  onCancelEdit?: () => void;
}
export function RealEstateForm({
  onAdd,
  partnerNames,
  loans,
  liquidityEvents,
  holdings = [],
  onAddLoan,
  onAddEvent,
  baseCurrency = 'USD',
  baseCountry = 'US',
  editingHolding,
  onEdit,
  onCancelEdit
}: RealEstateFormProps) {
  const [isExistingProperty, setIsExistingProperty] = useState(false);
  const [person, setPerson] = useState<string>(editingHolding?.person || partnerNames[0]);
  const [name, setName] = useState(editingHolding?.name || '');
  const [purchasePrice, setPurchasePrice] = useState(editingHolding?.purchasePrice?.toString() || '');
  const [currentValue, setCurrentValue] = useState(editingHolding?.amount?.toString() || '');
  const [appreciationRate, setAppreciationRate] = useState(editingHolding?.appreciationRate?.toString() || '3.0');
  const [capitalGainsTaxRate, setCapitalGainsTaxRate] = useState(editingHolding?.capitalGainsTaxRate?.toString() || '0');
  const [currency, setCurrency] = useState<Currency>(editingHolding?.currency || baseCurrency);
  const [country, setCountry] = useState<string>(editingHolding?.country || baseCountry);
  const [linkedLoanId, setLinkedLoanId] = useState<string>(editingHolding?.linkedLoanId || 'none');
  const [linkedEventId, setLinkedEventId] = useState<string>(editingHolding?.linkedEventIds?.[0] || 'none');
  // Closing / acquisition costs
  const [notaryCosts, setNotaryCosts] = useState('');
  const [stampDuty, setStampDuty] = useState('');
  const [transferTax, setTransferTax] = useState('');
  const [otherCosts, setOtherCosts] = useState(editingHolding?.closingCosts?.toString() || '');
  // Real estate strategy
  const [realEstateStrategy, setRealEstateStrategy] = useState<'hold' | 'sell'>(editingHolding?.realEstateStrategy || 'hold');
  const [plannedSaleYear, setPlannedSaleYear] = useState(editingHolding?.plannedSaleYear?.toString() || '10');
  // Funding source for closing costs
  const [closingCostsFundedFromHoldingId, setClosingCostsFundedFromHoldingId] = useState<string>(editingHolding?.closingCostsFundedFromHoldingId || 'none');
  const liquidHoldings = useMemo(() => {
    return holdings.filter(h => h.type === 'Liquid' && h.amount > 0);
  }, [holdings]);
  // Modal states
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const totalClosingCosts = (Number(notaryCosts) || 0) + (Number(stampDuty) || 0) + (Number(transferTax) || 0) + (Number(otherCosts) || 0);
  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const defaultCurr = getDefaultCurrency(newCountry);
    if (defaultCurr) {
      setCurrency(defaultCurr);
    }
  };
  // Live Equity Calculation
  const linkedLoan = useMemo(() => {
    return loans.find(l => l.id === linkedLoanId);
  }, [loans, linkedLoanId]);
  const outstandingLoanBalance = useMemo(() => {
    if (!linkedLoan) return 0;
    return Math.max(0, linkedLoan.totalAmount - linkedLoan.depositAmount);
  }, [linkedLoan]);
  // Linked event for rental/income
  const linkedEvent = useMemo(() => {
    return liquidityEvents.find(e => e.id === linkedEventId);
  }, [liquidityEvents, linkedEventId]);
  const currentValNum = Number(currentValue) || 0;
  const netEquity = Math.max(0, currentValNum - outstandingLoanBalance);
  const monthlyLoanPayment = linkedLoan ? calculateMonthlyPayment(linkedLoan) : 0;
  // Monthly income from linked event
  const monthlyEventIncome = useMemo(() => {
    if (!linkedEvent) return 0;
    if (linkedEvent.type === 'monthly') return linkedEvent.amount;
    if (linkedEvent.type === 'yearly') return linkedEvent.amount / 12;
    return 0;
  }, [linkedEvent]);
  const netMonthlyCashflow = monthlyEventIncome - monthlyLoanPayment;
  // Filter events suitable for linking (monthly/yearly income events)
  const linkableEvents = useMemo(() => {
    return liquidityEvents.filter(e => (e.isActive ?? true) && e.type !== 'one-time' && e.amount > 0);
  }, [liquidityEvents]);
  const activeLoans = loans.filter(l => l.isActive);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !currentValue || isNaN(Number(currentValue))) return;
    const finalClosingCosts = isExistingProperty ? 0 : totalClosingCosts;
    const finalFundedFrom = isExistingProperty ? undefined : closingCostsFundedFromHoldingId !== 'none' ? closingCostsFundedFromHoldingId : undefined;
    const updates = {
      person,
      name,
      amount: Number(currentValue),
      currency,
      type: 'Illiquid' as const,
      country,
      isRealEstate: true,
      purchasePrice: Number(purchasePrice) || 0,
      appreciationRate: Number(appreciationRate) || 0,
      capitalGainsTaxRate: Number(capitalGainsTaxRate) || 0,
      linkedLoanId: linkedLoanId !== 'none' ? linkedLoanId : undefined,
      linkedEventIds: linkedEventId !== 'none' ? [linkedEventId] : undefined,
      rentalIncome: monthlyEventIncome > 0 ? monthlyEventIncome : 0,
      closingCosts: finalClosingCosts > 0 ? finalClosingCosts : undefined,
      closingCostsFundedFromHoldingId: finalFundedFrom,
      realEstateStrategy,
      plannedSaleYear: realEstateStrategy === 'sell' ? Number(plannedSaleYear) || 10 : undefined,
      // Map to legacy drawdown fields for backward compat with projection engine
      drawdownAvailableYear: realEstateStrategy === 'sell' ? Number(plannedSaleYear) || 10 : 999,
      yieldsAfterDrawdown: realEstateStrategy === 'hold'
    };
    if (editingHolding && onEdit) {
      onEdit(editingHolding.id, updates);
    } else {
      onAdd(updates);
    }
    // Reset form
    if (!editingHolding) {
      setName('');
      setPurchasePrice('');
      setCurrentValue('');
      setAppreciationRate('3.0');
      setCapitalGainsTaxRate('0');
      setLinkedLoanId('none');
      setLinkedEventId('none');
      setClosingCostsFundedFromHoldingId('none');
      setNotaryCosts('');
      setStampDuty('');
      setTransferTax('');
      setOtherCosts('');
      setRealEstateStrategy('hold');
      setPlannedSaleYear('10');
    }
  };
  return <>
      <Card data-id="element-1426">
        <CardHeader title={editingHolding ? 'Edit Real Estate Asset' : 'Add Real Estate Asset'} subtitle={editingHolding ? 'Update property details and recalculate equity' : 'Track properties, link mortgages, and project equity'} data-id="element-1427" />
        <CardContent data-id="element-1428">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1429">
            {!editingHolding && <div className="flex bg-slate-100 p-1 rounded-lg mb-2" data-id="element-1430">
                <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${!isExistingProperty ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsExistingProperty(false)} data-id="element-1431">
                  🆕 New Purchase
                </button>
                <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${isExistingProperty ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsExistingProperty(true)} data-id="element-1432">
                  🏠 Historical Purchase
                </button>
              </div>}

            {isExistingProperty && !editingHolding && <div className="bg-blue-50 border border-blue-100 text-blue-700 p-3 rounded-lg text-sm flex gap-2 items-start" data-id="element-1433">
                <Info className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1434" />
                <p data-id="element-1435">
                  Enter the current market value. Sunk costs like closing fees
                  are ignored for historical purchases since they don't affect
                  your current liquid net worth.
                </p>
              </div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1436">
              <div data-id="element-1437">
                <Label data-id="element-1438">Property Name / Address</Label>
                <Input placeholder="e.g. 123 Main St, Rental Condo" value={name} onChange={e => setName(e.target.value)} required data-id="element-1439" />
              </div>
              <div data-id="element-1440">
                <Label data-id="element-1441">Owner</Label>
                <Select value={person} onChange={e => setPerson(e.target.value)} options={[{
                value: partnerNames[0],
                label: partnerNames[0]
              }, {
                value: partnerNames[1],
                label: partnerNames[1]
              }, {
                value: 'Joint',
                label: '🤝 Mutual (Joint)'
              }]} data-id="element-1442" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1443">
              <div data-id="element-1444">
                <Label data-id="element-1445">Country</Label>
                <Select searchable value={country} onChange={e => handleCountryChange(e.target.value)} options={COUNTRIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.name}`
              }))} data-id="element-1446" />
              </div>
              <div data-id="element-1447">
                <Label data-id="element-1448">Currency</Label>
                <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.code} (${c.symbol})`
              }))} data-id="element-1449" />
              </div>
            </div>

            {/* Valuation */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1450">
              <h4 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-1451">
                Valuation
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-id="element-1452">
                <div data-id="element-1453">
                  <Label data-id="element-1454">
                    {isExistingProperty ? 'Original Purchase Price' : 'Purchase Price'}
                  </Label>
                  <Input type="number" min="0" step="1000" placeholder="0" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} data-id="element-1455" />
                </div>
                <div data-id="element-1456">
                  <Label data-id="element-1457">
                    {isExistingProperty ? 'Current Market Value' : 'Current Value'}
                  </Label>
                  <Input type="number" min="0" step="1000" placeholder="0" value={currentValue} onChange={e => setCurrentValue(e.target.value)} required data-id="element-1458" />
                </div>
                <div data-id="element-1459">
                  <Label data-id="element-1460">Annual Apprec. (%)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="3.0" value={appreciationRate} onChange={e => setAppreciationRate(e.target.value)} data-id="element-1461" />
                </div>
                <div data-id="element-1462">
                  <Label data-id="element-1463">Capital Gains Tax (%)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="0.0" value={capitalGainsTaxRate} onChange={e => setCapitalGainsTaxRate(e.target.value)} data-id="element-1464" />
                </div>
              </div>
            </div>

            {/* Financing */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1465">
              <h4 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-1466">
                Linked Mortgage / Loan
              </h4>
              <div className="flex items-end gap-3" data-id="element-1467">
                <div className="flex-1" data-id="element-1468">
                  <Label data-id="element-1469">Select Existing Loan</Label>
                  <Select value={linkedLoanId} onChange={e => setLinkedLoanId(e.target.value)} options={[{
                  value: 'none',
                  label: 'None (Paid off / Cash)'
                }, ...activeLoans.map(l => ({
                  value: l.id,
                  label: `${l.name} (${formatCurrency(l.totalAmount - l.depositAmount, l.currency)})`
                }))]} data-id="element-1470" />
                </div>
                {onAddLoan && <Button type="button" variant="secondary" className="shrink-0 mb-0" onClick={() => setShowLoanModal(true)} data-id="element-1471">
                    <Plus className="w-4 h-4 mr-1.5" data-id="element-1472" />
                    New Loan
                  </Button>}
              </div>
              {linkedLoan && <div className="mt-3 grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 text-sm" data-id="element-1473">
                  <div data-id="element-1474">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1475">Principal</p>
                    <p className="font-medium text-slate-900" data-id="element-1476">
                      {formatCurrency(outstandingLoanBalance, linkedLoan.currency)}
                    </p>
                  </div>
                  <div data-id="element-1477">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1478">
                      Monthly Payment
                    </p>
                    <p className="font-bold text-rose-600" data-id="element-1479">
                      -{formatCurrency(monthlyLoanPayment, linkedLoan.currency)}
                      /mo
                    </p>
                  </div>
                  <div data-id="element-1480">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1481">
                      Total Interest
                    </p>
                    <p className="font-medium text-rose-500" data-id="element-1482">
                      {formatCurrency(calculateTotalInterest(linkedLoan), linkedLoan.currency)}
                    </p>
                  </div>
                </div>}
            </div>

            {/* Linked Income / Cash Event */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1483">
              <h4 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-1484">
                Linked Cash Income
              </h4>
              <div className="flex items-end gap-3" data-id="element-1485">
                <div className="flex-1" data-id="element-1486">
                  <Label data-id="element-1487">
                    Select Existing Cash Event (Rental Income, etc.)
                  </Label>
                  <Select value={linkedEventId} onChange={e => setLinkedEventId(e.target.value)} options={[{
                  value: 'none',
                  label: 'None (No recurring income)'
                }, ...linkableEvents.map(ev => ({
                  value: ev.id,
                  label: `${ev.name} (+${formatCurrency(ev.amount, ev.currency)}/${ev.type === 'monthly' ? 'mo' : 'yr'})`
                }))]} data-id="element-1488" />
                </div>
                {onAddEvent && <Button type="button" variant="secondary" className="shrink-0 mb-0" onClick={() => setShowEventModal(true)} data-id="element-1489">
                    <Plus className="w-4 h-4 mr-1.5" data-id="element-1490" />
                    New Event
                  </Button>}
              </div>
              {linkedEvent && <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-sm flex items-center justify-between" data-id="element-1491">
                  <span className="text-emerald-700 font-medium" data-id="element-1492">
                    {linkedEvent.name}
                  </span>
                  <span className="font-bold text-emerald-800" data-id="element-1493">
                    +{formatCurrency(linkedEvent.amount, linkedEvent.currency)}/
                    {linkedEvent.type === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>}
            </div>

            {/* Closing & Acquisition Costs */}
            {!isExistingProperty && <div className="border-t border-slate-100 pt-4" data-id="element-1494">
                <h4 className="text-sm font-semibold text-slate-800 mb-1" data-id="element-1495">
                  Closing & Acquisition Costs
                </h4>
                <p className="text-xs text-slate-400 mb-4" data-id="element-1496">
                  One-time sunk costs that don't add property value — recorded
                  as a cash outflow.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-1497">
                  <div data-id="element-1498">
                    <Label data-id="element-1499">Notary Fees</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={notaryCosts} onChange={e => setNotaryCosts(e.target.value)} data-id="element-1500" />
                  </div>
                  <div data-id="element-1501">
                    <Label data-id="element-1502">Stamp Duty</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={stampDuty} onChange={e => setStampDuty(e.target.value)} data-id="element-1503" />
                  </div>
                  <div data-id="element-1504">
                    <Label data-id="element-1505">Transfer Tax</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={transferTax} onChange={e => setTransferTax(e.target.value)} data-id="element-1506" />
                  </div>
                  <div data-id="element-1507">
                    <Label data-id="element-1508">Other Costs</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={otherCosts} onChange={e => setOtherCosts(e.target.value)} data-id="element-1509" />
                  </div>
                </div>
                {totalClosingCosts > 0 && <>
                    <div className="mt-3 flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg border border-amber-100" data-id="element-1510">
                      <span className="text-xs font-semibold text-amber-700" data-id="element-1511">
                        Total Closing Costs
                      </span>
                      <span className="text-sm font-bold text-amber-800" data-id="element-1512">
                        {formatCurrency(totalClosingCosts, currency)}
                      </span>
                    </div>
                    {liquidHoldings.length > 0 && <div className="mt-3" data-id="element-1513">
                        <Label data-id="element-1514">Fund Closing Costs From</Label>
                        <Select searchable value={closingCostsFundedFromHoldingId} onChange={e => setClosingCostsFundedFromHoldingId(e.target.value)} options={[{
                  value: 'none',
                  label: "Don't deduct from holdings (manual / external)"
                }, ...liquidHoldings.map(h => ({
                  value: h.id,
                  label: `${h.name} (${formatCurrency(h.amount, h.currency)})`
                }))]} data-id="element-1515" />
                        {closingCostsFundedFromHoldingId !== 'none' && <p className="text-xs text-amber-600 mt-1.5" data-id="element-1516">
                            ⚠ {formatCurrency(totalClosingCosts, currency)}{' '}
                            will be deducted from this holding.
                          </p>}
                      </div>}
                  </>}
              </div>}

            {/* Exit Strategy */}
            <div className="border-t border-slate-100 pt-4" data-id="element-1517">
              <h4 className="text-sm font-semibold text-slate-800 mb-1" data-id="element-1518">
                Exit Strategy
              </h4>
              <p className="text-xs text-slate-400 mb-4" data-id="element-1519">
                How does this property factor into your long-term financial
                plan?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1520">
                <div data-id="element-1521">
                  <Label data-id="element-1522">Strategy</Label>
                  <div className="flex bg-slate-100 p-1 rounded-lg mt-1" data-id="element-1523">
                    <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${realEstateStrategy === 'hold' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setRealEstateStrategy('hold')} data-id="element-1524">
                      🏠 Hold Long-term
                    </button>
                    <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${realEstateStrategy === 'sell' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setRealEstateStrategy('sell')} data-id="element-1525">
                      💰 Plan to Sell
                    </button>
                  </div>
                </div>
                {realEstateStrategy === 'sell' && <div data-id="element-1526">
                    <Label data-id="element-1527">Planned Sale Year (from now)</Label>
                    <Input type="number" min="0" step="1" placeholder="10" value={plannedSaleYear} onChange={e => setPlannedSaleYear(e.target.value)} data-id="element-1528" />
                    <p className="text-xs text-slate-400 mt-1" data-id="element-1529">
                      Sale proceeds (minus loan & CGT) enter your cash pool
                    </p>
                  </div>}
              </div>
              <div className="mt-3 p-3 rounded-lg border text-sm" data-id="element-1530">
                {realEstateStrategy === 'hold' ? <div className="bg-blue-50 border-blue-100 text-blue-700 p-3 rounded-lg" data-id="element-1531">
                    <p className="font-medium mb-1" data-id="element-1532">
                      📊 Equity only — not spendable
                    </p>
                    <p className="text-xs text-blue-600" data-id="element-1533">
                      This property appreciates and contributes to your net
                      worth, but won't be sold to fund retirement. Rental income
                      (if linked) still flows as cash.
                    </p>
                  </div> : <div className="bg-amber-50 border-amber-100 text-amber-700 p-3 rounded-lg" data-id="element-1534">
                    <p className="font-medium mb-1" data-id="element-1535">
                      💰 Investment exit — becomes cash
                    </p>
                    <p className="text-xs text-amber-600" data-id="element-1536">
                      At year {plannedSaleYear || '?'}, the appreciated value
                      (minus outstanding loan and capital gains tax) converts to
                      spendable cash. Appreciation and rental income stop after
                      sale.
                    </p>
                  </div>}
              </div>
            </div>

            {/* Live Equity Summary */}
            {currentValNum > 0 && <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200 flex flex-col gap-3" data-id="element-1537">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1" data-id="element-1538">
                  <Calculator className="w-4 h-4 text-blue-500" data-id="element-1539" />
                  Real Estate Summary Preview
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm" data-id="element-1540">
                  <div data-id="element-1541">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1542">
                      Current Value
                    </p>
                    <p className="font-medium text-slate-900" data-id="element-1543">
                      {formatCurrency(currentValNum, currency)}
                    </p>
                  </div>
                  <div data-id="element-1544">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1545">
                      Outstanding Loan
                    </p>
                    <p className="font-medium text-rose-600" data-id="element-1546">
                      {linkedLoan ? `-${formatCurrency(outstandingLoanBalance, currency)}` : 'None'}
                    </p>
                  </div>
                  <div data-id="element-1547">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1548">Net Equity</p>
                    <p className="font-bold text-emerald-700" data-id="element-1549">
                      {formatCurrency(netEquity, currency)}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mt-3 pt-3 border-t border-slate-200" data-id="element-1550">
                  {!isExistingProperty && totalClosingCosts > 0 && <div data-id="element-1551">
                      <p className="text-slate-500 text-xs mb-0.5" data-id="element-1552">
                        Sunk Costs
                      </p>
                      <p className="font-medium text-amber-700" data-id="element-1553">
                        -{formatCurrency(totalClosingCosts, currency)}
                      </p>
                    </div>}
                  <div data-id="element-1554">
                    <p className="text-slate-500 text-xs mb-0.5" data-id="element-1555">
                      Net Monthly Cashflow
                    </p>
                    <p className={`font-medium ${netMonthlyCashflow >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1556">
                      {netMonthlyCashflow > 0 ? '+' : ''}
                      {formatCurrency(netMonthlyCashflow, currency)}
                    </p>
                  </div>
                  {!isExistingProperty && <div data-id="element-1557">
                      <p className="text-slate-500 text-xs mb-0.5" data-id="element-1558">
                        Total Cash Out
                      </p>
                      <p className="font-bold text-slate-800" data-id="element-1559">
                        {formatCurrency((Number(purchasePrice) || currentValNum) + totalClosingCosts, currency)}
                      </p>
                    </div>}
                </div>
              </div>}

            <div className="flex gap-3 mt-4" data-id="element-1560">
              <Button type="submit" className="flex-1" data-id="element-1561">
                <Building2 className="w-4 h-4 mr-2" data-id="element-1562" />
                {editingHolding ? 'Save Changes' : 'Add Real Estate Asset'}
              </Button>
              {editingHolding && onCancelEdit && <Button type="button" variant="secondary" onClick={onCancelEdit} data-id="element-1563">
                  Cancel
                </Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Create Loan Modal */}
      <Modal isOpen={showLoanModal} onClose={() => setShowLoanModal(false)} title="Create New Loan" subtitle="This loan will be available to link to your property" data-id="element-1564">
        <LoanForm holdings={holdings} onAdd={loan => {
        if (onAddLoan) {
          const newId = onAddLoan(loan);
          setShowLoanModal(false);
          if (newId) {
            setLinkedLoanId(newId);
          }
        }
      }} partnerNames={partnerNames} initialValues={{
        name: name ? `${name} Mortgage` : '',
        currency,
        person,
        totalAmount: purchasePrice || currentValue || ''
      }} data-id="element-1565" />
      </Modal>

      {/* Create Cash Event Modal */}
      <Modal isOpen={showEventModal} onClose={() => setShowEventModal(false)} title="Create New Cash Event" subtitle="Create a recurring income event to link to this property" data-id="element-1566">
        <LiquidityEventForm onAdd={event => {
        if (onAddEvent) {
          const newId = onAddEvent(event);
          setShowEventModal(false);
          if (newId) {
            setLinkedEventId(newId);
          }
        }
      }} initialValues={{
        name: name ? `${name} Rental Income` : '',
        currency,
        type: 'monthly',
        isPositive: true,
        startYear: '0'
      }} data-id="element-1567" />
      </Modal>
    </>;
}